import type { RoomView } from "@shared/types";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { TimerBar } from "../../components/Timer";
import { useI18n } from "../../i18n";
import { asset } from "../../lib/asset";
import type { RoomClient } from "../../lib/room-client";
import { avatarVoice, haptic, play } from "../../lib/sfx";
import { FinalResults, GameChrome } from "../common/GameKit";
import { useGameAct } from "../common/useRoom";

interface Card {
  id: string;
  state: "down" | "up" | "matched" | "boom";
  face: number | null;
  by: string | null;
}
export interface MatchV {
  phase: "turn" | "show" | "over";
  deadline: number | null;
  phaseMs: number;
  theme: string;
  cards: Card[];
  order: string[];
  current: string;
  myTurn: boolean;
  flipped: string[];
  scores: Record<string, number>;
  pairsOf: Record<string, number>;
  streak: number;
  lastEvent: { kind: "match" | "miss" | "boom" | "timeout"; by: string; points: number; at: number } | null;
  totalPairs: number;
  left: number;
}

const EMOJI: Record<string, string[]> = {
  animals: ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🦜", "🦆", "🦉", "🐴", "🦄", "🐝", "🐢"],
  party: ["🎉", "🎈", "🎂", "🍰", "🎁", "🎊", "🍾", "🥂", "🍕", "🍔", "🌭", "🍿", "🎤", "🎧", "🎸", "🎷", "🥁", "🎲", "🎯", "🎳", "🪩", "🕺", "💃", "🎭"],
  tbilisi: ["🌋", "🌈", "⚡", "❄️", "🔥", "🌙", "⭐", "☀️", "🍀", "🌵", "🌸", "🍄", "🌊", "💎", "🚀", "🛸", "⚓", "🗿", "🏰", "🎡", "🎢", "🎪", "🧲", "🪐"],
  emoji: ["😀", "😂", "🥹", "😍", "🤩", "😎", "🤓", "🥳", "😴", "🤯", "🥶", "🤠", "🤡", "👻", "👽", "🤖", "😈", "😇", "🤪", "😜", "🙃", "🫠", "😱", "🥸"],
};

function Face({ theme, face }: { theme: string; face: number }) {
  const [broken, setBroken] = useState(false);
  if (theme === "supra" && !broken) {
    return (
      <img
        src={asset(`img/cards/supra/${String(face).padStart(2, "0")}.webp`)}
        alt=""
        draggable={false}
        className="h-[86%] w-[86%] object-contain"
        onError={() => setBroken(true)}
      />
    );
  }
  const set = EMOJI[theme] ?? EMOJI.animals;
  return <span className="text-[clamp(26px,7vw,54px)] leading-none">{set[face % set.length]}</span>;
}

export function MatchView({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const v = view.view as MatchV;
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const [pop, setPop] = useState<{ text: string; tone: string; key: number } | null>(null);
  const lastAt = useRef<number>(v.lastEvent?.at ?? 0);
  const prevFlipped = useRef<number>(v.flipped.length);

  // sounds + popups for game events
  useEffect(() => {
    const e = v.lastEvent;
    if (!e || e.at === lastAt.current) return;
    lastAt.current = e.at;
    const who = view.players.find((p) => p.id === e.by);
    if (e.kind === "match") {
      play("match");
      if (v.streak >= 2) play("streak", { delay: 0.25 });
      if (who) avatarVoice(who.avatar, { delay: 0.45, volume: 0.8 });
      setPop({ text: v.streak >= 2 ? f(d.match.combo, { n: v.streak, p: e.points }) : f(d.match.match, { n: e.points }), tone: "var(--color-good)", key: e.at });
    } else if (e.kind === "boom") {
      play("boom");
      haptic([60, 40, 120]);
      setPop({ text: f(d.match.boom, { n: Math.abs(e.points) }), tone: "var(--color-candy)", key: e.at });
    } else if (e.kind === "timeout") {
      play("timeup");
      setPop({ text: d.match.timeout, tone: "var(--color-flame)", key: e.at });
    } else {
      play("wrong", { volume: 0.6 });
      setPop({ text: d.match.miss, tone: "var(--color-ground-5)", key: e.at });
    }
    const t = setTimeout(() => setPop(null), 1300);
    return () => clearTimeout(t);
  }, [v.lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (v.flipped.length > prevFlipped.current) play("flip");
    prevFlipped.current = v.flipped.length;
  }, [v.flipped.length]);

  if (v.phase === "over") {
    return (
      <>
        <GameChrome client={client} view={view} />
        <FinalResults client={client} view={view} rows={v.order.map((id) => ({ id, score: v.scores[id] ?? 0, sub: `${d.match.pairs}: ${v.pairsOf[id] ?? 0}` }))} />
      </>
    );
  }

  const current = view.players.find((p) => p.id === v.current);
  const n = v.cards.length;
  const cols = n <= 16 ? 4 : n <= 20 ? 5 : n <= 30 ? 6 : n <= 42 ? 7 : 8;
  const canFlip = !tv && v.phase === "turn" && v.myTurn;

  return (
    <>
      <GameChrome client={client} view={view} />
      <div className="shell grid gap-5 pt-2 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {current && <Avatar index={current.avatar} color={current.color} size={52} mood="talk" />}
            <p className="display min-w-0 flex-1 truncate text-2xl sm:text-3xl">
              {v.myTurn && !client.local ? d.match.yourTurn : f(d.match.turn, { name: current?.name ?? "" })}
            </p>
            <span className="font-bold text-ink-3">{f(d.match.pairsLeft, { n: v.left })}</span>
          </div>
          {v.deadline && v.phase === "turn" && <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-match)" tickLast={3} />}
          <div className="relative">
            <div className="grid gap-2 sm:gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {v.cards.map((c) => {
                const up = c.state !== "down";
                const gone = c.state === "matched" || c.state === "boom";
                const owner = c.by ? view.players.find((p) => p.id === c.by) : null;
                return (
                  <button
                    key={c.id}
                    className="flip-scene aspect-[3/4] w-full"
                    disabled={!canFlip || c.state !== "down"}
                    onClick={() => {
                      haptic(8);
                      void g({ t: "flip", card: c.id }, true);
                    }}
                    aria-label={up ? "card" : "hidden card"}
                  >
                    <motion.div
                      className="flip-card"
                      data-flipped={up ? "true" : "false"}
                      // Motion writes transform inline. Keep rotateY in the same motion
                      // transform so it is never overwritten by scale/opacity animation.
                      animate={{
                        rotateY: up ? 180 : 0,
                        scale: gone ? (c.state === "boom" ? [1, 1.25, 0.9] : 0.92) : 1,
                        opacity: gone ? (c.state === "boom" ? 0.35 : 0.88) : 1,
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="flip-face grid place-items-center bg-ground-4" style={{ boxShadow: "inset 0 -5px 0 rgba(0,0,0,.25)" }}>
                        <CardBack />
                      </div>
                      <div
                        className="flip-face flip-back grid place-items-center"
                        style={{
                          background: c.state === "boom" ? "var(--color-candy)" : c.state === "matched" ? `color-mix(in oklab, ${owner ? "var(--color-good)" : "#fff"} 30%, var(--color-ink))` : "var(--color-ink)",
                        }}
                      >
                        {c.face === -1 ? <span className="text-[clamp(26px,7vw,54px)]">💣</span> : c.face !== null ? <Face theme={v.theme} face={c.face} /> : null}
                        {c.state === "matched" && owner && (
                          <span className="absolute -right-1 -bottom-1">
                            <Avatar index={owner.avatar} color={owner.color} size={26} />
                          </span>
                        )}
                      </div>
                    </motion.div>
                  </button>
                );
              })}
            </div>
            <AnimatePresence>
              {pop && (
                <motion.div
                  key={pop.key}
                  initial={{ scale: 0.4, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 1.2, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 420, damping: 16 }}
                  className="pointer-events-none absolute inset-0 grid place-items-center"
                >
                  <span className="display rounded-full px-6 py-3 text-3xl sm:text-4xl" style={{ background: pop.tone, color: "#160626" }}>{pop.text}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <aside className="surface h-fit space-y-2 p-4">
          {v.order.map((id) => {
            const p = view.players.find((x) => x.id === id);
            if (!p) return null;
            const on = id === v.current;
            return (
              <div key={id} className="flex items-center gap-3 rounded-2xl px-3 py-2" style={{ background: on ? "color-mix(in oklab, var(--color-match) 28%, var(--color-ground-3))" : "var(--color-ground-3)" }}>
                <Avatar index={p.avatar} color={p.color} size={42} mood={on ? "talk" : "still"} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-extrabold">{p.name}</span>
                  <span className="block text-sm text-ink-3">{d.match.pairs}: {v.pairsOf[id] ?? 0}</span>
                </span>
                <span className="tabular text-xl font-black">{v.scores[id] ?? 0}</span>
              </div>
            );
          })}
        </aside>
      </div>
    </>
  );
}

function CardBack() {
  return (
    <svg viewBox="0 0 60 80" className="h-[70%] w-[70%]" aria-hidden>
      <rect x="4" y="4" width="52" height="72" rx="10" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="3" />
      <path d="M30 18c6 8 12 13 12 23a12 12 0 1 1-24 0c0-5 2-9 5-12 0 5 2 8 5 9-1-8 0-14 2-20z" fill="var(--color-match)" />
      <path d="M30 34c3 4 6 6 6 11a6 6 0 1 1-12 0c0-3 2-6 4-7 0 3 1 4 2 5 0-4 0-6 0-9z" fill="var(--color-flame)" />
    </svg>
  );
}
